import { useEffect, useMemo, useState, Fragment } from "react";
import { ArrowDownLeft, ArrowUpRight, Gift, RefreshCcw, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, inrPaise, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const QUICK = [500, 1000, 2000, 5000];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState(null);
  const [offer, setOffer] = useState(null);
  const [custom, setCustom] = useState("");
  const [paying, setPaying] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  /* date-wise grouping of the full transaction feed */
  const filteredTxns = useMemo(() => {
    return (txns || []).filter((t) => {
      if (typeFilter && t.type !== typeFilter) return false;
      const d = new Date(t.created_at);
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (d > end) return false;
      }
      return true;
    });
  }, [txns, dateFrom, dateTo, typeFilter]);

  const grouped = useMemo(() => {
    const groups = [];
    for (const t of filteredTxns) {
      const day = new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      let g = groups.find((x) => x.day === day);
      if (!g) {
        g = { day, items: [], net: 0 };
        groups.push(g);
      }
      g.items.push(t);
      g.net += (t.type === "credit" ? 1 : -1) * (t.amount_paise || 0);
    }
    return groups;
  }, [filteredTxns]);

  const downloadCsv = () => {
    const rows = [
      ["Date", "Time", "Type", "Source", "Note", "Amount (INR)"],
      ...filteredTxns.map((t) => {
        const d = new Date(t.created_at);
        return [
          d.toLocaleDateString("en-GB"),
          d.toLocaleTimeString("en-IN"),
          t.type,
          t.source || "",
          (t.note || "").replace(/[",\n]/g, " "),
          ((t.type === "credit" ? 1 : -1) * (t.amount_paise || 0) / 100).toFixed(2),
        ];
      }),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `shipzilla-wallet-statement-${dateFrom || "all"}-${dateTo || "today"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Statement downloaded.");
  };

  const load = () => {
    appApi.get("wallet").then(setWallet).catch(() => {});
    appApi.get("wallet/transactions").then((d) => setTxns(d.transactions || [])).catch(() => setTxns([]));
    appApi.get("wallet/first-recharge-offer").then(setOffer).catch(() => {});
  };
  useEffect(load, []);

  const topup = async (amount) => {
    if (!amount || amount <= 0) return;
    setPaying(true);
    try {
      const intent = await appApi.post("wallet/topup", { amount_inr: amount });
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load Razorpay. Check your connection.");
        return;
      }
      const rzp = new window.Razorpay({
        key: intent.key_id,
        order_id: intent.order_id,
        amount: intent.amount_paise,
        currency: intent.currency || "INR",
        name: "ShipZilla",
        description: "Wallet recharge",
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#2563EB" },
        modal: { ondismiss: () => toast.info("Payment cancelled — no money moved.") },
        handler: async (resp) => {
          try {
            await appApi.post("wallet/topup/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`${inr(amount)} added to your wallet.`);
            load();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed — contact support.");
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed.");
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPaying(false);
    }
  };

  const w = wallet?.wallet;

  return (
    <div data-testid="wallet-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Wallet</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Fuel tank</h1>
        </div>
        <button onClick={load} data-testid="wallet-refresh-button"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border-2 border-obsidian px-4 py-2.5 bg-white hover:bg-acid hover:text-white transition-colors duration-150">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {offer?.eligible && (
        <div data-testid="wallet-offer-banner" className="border-2 border-obsidian bg-acid px-5 py-4 flex items-center gap-3 font-semibold text-sm text-white">
          <Gift className="w-5 h-5" />
          First recharge offer: top up {inr(offer.min_recharge_inr)}+ and get {offer.cashback_pct}% cashback.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="border-2 border-obsidian bg-obsidian text-white p-8 shadow-[8px_8px_0px_0px_#2563EB]">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-400">Available balance</p>
          <p className="font-display font-black text-5xl tracking-tight mt-3 text-sky-300" data-testid="wallet-balance">
            {w ? inr(w.balance_inr) : "—"}
          </p>
          <p className="text-xs text-zinc-500 mt-4 font-mono">{w?.currency || "INR"} · Synced live with the app</p>
        </div>

        <div className="lg:col-span-2 border-2 border-obsidian bg-white p-8">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Recharge via Razorpay</p>
          <div className="flex flex-wrap gap-3 mt-5">
            {QUICK.map((q) => (
              <button
                key={q}
                onClick={() => topup(q)}
                disabled={paying}
                data-testid={`topup-btn-${q}`}
                className="bg-acid text-white font-bold px-6 py-3 border-2 border-obsidian shadow-[3px_3px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-60"
              >
                + {inr(q)}
              </button>
            ))}
          </div>
          <div className="flex gap-3 mt-5">
            <input
              type="number"
              min="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom amount"
              data-testid="topup-custom-input"
              className="flex-1 border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
            />
            <button
              onClick={() => topup(parseFloat(custom))}
              disabled={paying || !custom || parseFloat(custom) <= 0}
              data-testid="topup-custom-button"
              className="inline-flex items-center gap-2 bg-obsidian text-sky-300 font-bold px-6 py-3 border-2 border-obsidian hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-60"
            >
              <Plus className="w-4 h-4" /> Pay
            </button>
          </div>
          <p className="text-xs text-zinc-400 mt-4 font-mono" data-testid="wallet-auto-recharge">
            Auto-recharge: {w?.auto_recharge_enabled
              ? `ON — tops up ${inr(w.auto_recharge_amount_inr)} below ${inr(w.auto_recharge_threshold_inr)}`
              : "OFF (manage in the app)"}
          </p>
        </div>
      </div>

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <div className="px-5 py-4 border-b-2 border-obsidian flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Transactions — date wise</p>
          <div className="flex items-center gap-2 flex-wrap" data-testid="wallet-txn-filters">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="wallet-date-from"
              className="border-2 border-obsidian bg-paper px-3 py-1.5 text-xs outline-none" />
            <span className="text-xs font-mono text-zinc-400">to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="wallet-date-to"
              className="border-2 border-obsidian bg-paper px-3 py-1.5 text-xs outline-none" />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="wallet-type-filter"
              className="border-2 border-obsidian bg-paper px-3 py-1.5 text-xs outline-none">
              <option value="">All</option>
              <option value="credit">Credits</option>
              <option value="debit">Debits</option>
            </select>
            <button onClick={downloadCsv} disabled={filteredTxns.length === 0} data-testid="wallet-download-csv"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 border-2 border-obsidian bg-obsidian text-sky-300 disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>
        <table className="w-full text-sm min-w-[560px]" data-testid="wallet-transactions-table">
          <tbody>
            {grouped.map((g) => (
              <Fragment key={g.day}>
                <tr className="bg-paper border-y border-zinc-200" data-testid={`wallet-day-${g.day}`}>
                  <td colSpan={4} className="px-5 py-2.5">
                    <span className="font-mono text-xs font-bold uppercase tracking-widest text-obsidian">{g.day}</span>
                    <span className={`ml-3 font-mono text-xs font-bold ${g.net >= 0 ? "text-green-700" : "text-blaze"}`}>
                      net {g.net >= 0 ? "+" : "−"}{inrPaise(Math.abs(g.net))}
                    </span>
                    <span className="ml-3 text-[10px] font-mono text-zinc-400">{g.items.length} movement{g.items.length > 1 ? "s" : ""}</span>
                  </td>
                </tr>
                {g.items.map((t) => (
                  <tr key={t.id} className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100" data-testid={`wallet-txn-${t.id}`}>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 font-mono text-xs font-bold ${t.type === "credit" ? "text-green-700" : "text-blaze"}`}>
                        {t.type === "credit" ? <ArrowDownLeft className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                        {t.type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-zinc-600">{t.note}</td>
                    <td className="px-5 py-3 font-mono text-xs text-zinc-500">{new Date(t.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</td>
                    <td className={`px-5 py-3 text-right font-mono text-xs font-bold ${t.type === "credit" ? "text-green-700" : ""}`}>
                      {t.type === "credit" ? "+" : "−"}{inrPaise(t.amount_paise)}
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        {txns && filteredTxns.length === 0 && <p className="px-5 py-10 text-sm text-zinc-500" data-testid="wallet-txn-empty">No transactions in this range.</p>}
        {txns && txns.length > 0 && (
          <p className="px-5 py-3 border-t border-zinc-100 text-[11px] font-mono text-zinc-400">
            Showing {filteredTxns.length} of the latest {txns.length} wallet movements — the app server keeps the 50 most recent. Download CSV for your records.
          </p>
        )}
      </div>
    </div>
  );
}
